import { useEffect, useRef, useState, useCallback } from "react";
import { Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function BookingChat({ bookingId, role, endpoint }) {
  const { t } = useTranslation();
  const base = endpoint || `/bookings/${bookingId}/messages`;
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(base);
      setMessages(r.data);
    } catch (e) { /* silent poll */ }
  }, [base]);

  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      const r = await api.post(base, { body });
      setMessages((m) => [...m, r.data]);
      setText("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div data-testid={`chat-${bookingId}`} className="flex flex-col h-80 bg-slate-50 rounded-xl border border-slate-100">
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <p className="text-slate-400 text-sm text-center mt-10">{t("chat.empty")}</p>
        ) : messages.map((m) => {
          const mine = m.sender_role === role;
          return (
            <div key={m.id} data-testid={`chat-msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${mine ? "bg-blue-600 text-white" : "bg-white text-slate-800 border border-slate-100"}`}>
                {!mine && <p className="text-xs font-semibold mb-0.5 text-slate-500">{m.sender_name}</p>}
                <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                <p className={`text-[10px] mt-1 ${mine ? "text-blue-100" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString()}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-slate-200 flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          data-testid={`chat-input-${bookingId}`}
          className="belo-input"
          placeholder={t("chat.placeholder")}
        />
        <button onClick={send} disabled={sending} data-testid={`chat-send-${bookingId}`} className="shrink-0 grid place-items-center w-12 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"><Send size={16} /></button>
      </div>
    </div>
  );
}
